<template>
  <div class="department-multi-check">
    <kt-autoselect
      :mode="props.mode"
      :label="props.label"
      :options="departmentOptions"
      display-by="label"
      value-by="value"
      :multiple="true"
      v-model="model"
      search-placeholder="Search department..."
      :empty-text="props.emptyText"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import OptionsService from '@/services/OptionsService';

interface DepartmentOption {
  label: string;
  value: string;
}

const props = defineProps({
  mode: {
    type: String,
    default: "dropdown"
  },
  label: { 
    type: String, 
    default: 'Select Department' 
  },
  filter: {
    type: Function as PropType<(dept: DepartmentOption) => boolean>,
    default: null
  },
  emptyText: {
    type: String,
    default: 'No Matching Departments'
  }
});

const emit = defineEmits<{
  change: [value: string[]];
}>();

const model = defineModel<string[]>({
  default: () => []
});

// Raw list loaded from OptionsService. `departmentOptions` derives from it
// so the filter prop is re-applied reactively when any reactive state it
// reads changes after initial mount.
const rawDepartments = ref<DepartmentOption[]>([]);

const departmentOptions = computed<DepartmentOption[]>(() =>
  props.filter ? rawDepartments.value.filter(props.filter) : rawDepartments.value
);

const DEPARTMENT_OPTION_KEY = 'departments_json';

const loadDepartmentOptions = async () => {
  try {
    const optionValue = await OptionsService.get(DEPARTMENT_OPTION_KEY);
    if (optionValue) {
      const depts = JSON.parse(optionValue);
      rawDepartments.value = depts.map((d: any) => ({ value: d.name, label: d.name }));
    } else {
      rawDepartments.value = [];
    }
  } catch (error) {
    console.error('Failed to load departments:', error);
    rawDepartments.value = [];
  }
};

onMounted(() => {
  loadDepartmentOptions();
});

const handleChange = (value: string[]) => {
  emit('change', value);
};
</script>
